import { UseAppFormProps } from "hooks/useAppForm";
import { Component, FC, Fragment, ReactNode, useMemo } from "react";
import Select from "./Select";
import { FormCodeEditor } from "./CodeEditor";
import Password from "./Password";
import Input from "./Input";
import Number from "./Number";
import Color from "./Color";
import Textarea from "./Textarea";
import DatePicker from "./DatePicker";
import Checkbox from "./Checkbox";
import Radio from "./Radio";
import UploadImage from "./UploadImage";
import Editor from "./Editor";
import Rate from "./Rate";
import useAppTranslation from "hooks/useAppTranslation";

interface AppFormProps<T> {
    id?: string;
    form: UseAppFormProps<T>
    onSubmit: (values: T) => void;
    children?: ReactNode;
    cols?: 1 | 2 | 3 | 4;
    noStyle?: boolean;
    shadow?: boolean;
    className?: string;
}
const Form = <T extends object>({
    id,
    form,
    onSubmit,
    children,
    cols,
    noStyle,
    shadow = true,
    ...props
}: AppFormProps<T>) => {

    const { validateForm, values } = form;
    const className = useMemo(() => {
        // let cls = noStyle ? `${props.className} ` : `bg-white p-4 rounded ${props.className}`;
        let cls = noStyle ? "" : `bg-white p-4 rounded`;
        cls += ` ${props.className || ""}`
        if (shadow) {
            cls += " shadow"
        }
        cls += " w-full";
        switch (cols) {
            case 2:
            case 3:
            case 4:
                return cls += ` grid grid-cols-1 md:grid-cols-${cols} gap-x-4`;
            default:
                return cls;

        }
    }, [cols, noStyle, props.className])
    return <Fragment>
        <form
            id={id}
            ref={form.formRef}
            className={className}
            onSubmit={(e) => {
                e.preventDefault();
                if (validateForm()) {
                    onSubmit(values);
                }
            }}
        >
            {children}
        </form>
    </Fragment>
}
interface ActionProps {
    col?: number;
    children?: ReactNode;
    form?: UseAppFormProps<any>
}
const Action: FC<ActionProps> = ({ col, children, form }) => {
    const { t } = useAppTranslation();
    const className = useMemo(() => {
        let className = "flex justify-end space-x-2 mt-2";
        if (col) {
            className += ` md:col-span-${col}`;
        }
        return className;
    }, [col])

    if (form && !children) {
        return <div className={className}>
            <button
                disabled={form.pristine}
                type="button"
                onClick={form.reset}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:bg-gray-200 disabled:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <i className="fa-solid fa-xmark mr-2"></i>
                <span>{t("cancel", { ns: "common" })}</span>
            </button>
            <button
                disabled={form.pristine || form.isError}
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:hover:bg-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <i className="fa-solid fa-floppy-disk mr-2"></i>
                <span>{t("save", { ns: "common" })}</span>
            </button>
        </div>
    }
    return <div className={className}>
        {children}
    </div>
}

export interface ItemProps {
    col?: number;
    label?: ReactNode;
    required?: boolean;
    children?: ReactNode;
    error?: string;
    className?: string;
    itemClassName?: string;
    readOnly?: boolean;
    helper?: ReactNode;
}
export const Item: FC<ItemProps> = ({ col, label, required, children, error, itemClassName = "", helper, className = "", ...props }) => {
    const _className = useMemo(() => {
        if (col) {
            return `relative mb-2 md:col-span-${col} ${itemClassName} ${className}`;
        }
        return `relative mb-2 ${itemClassName} ${className}`;
    }, [col, itemClassName, className])
    return <div className={_className}>
        {label && <label className="block text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>}
        {children}
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        {helper}
    </div>
}

class AppForm<T extends object> extends Component<AppFormProps<T>> {
    static Item = Item;
    static Input = Input;
    static Password = Password;
    static Number = Number;
    static Rate = Rate;
    static Color = Color;
    static Textarea = Textarea;
    static Select = Select;
    static DatePicker = DatePicker;
    static Editor = Editor;
    static Checkbox = Checkbox;
    static Radio = Radio;
    static CodeEditor = FormCodeEditor;
    static UploadImage = UploadImage;
    static Action = Action;
    render(): ReactNode {
        return <Form<T> {...this.props} />
    }
}
export default AppForm;